import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"
import { financialPostingLineV1Schema } from "./financial-posting-v1.js"

const existingDocumentSchema = z.object({
  documentType: z.enum(["Journal Entry", "Payment Entry", "Sales Invoice"]),
  documentId: z.string().trim().min(1).max(140),
}).strict()

export const financialCorrectionV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.literal(0),
  correctionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  kind: z.enum([
    "refund",
    "dispute",
    "chargeback",
    "write_off",
    "void",
    "operator_correction",
  ]),
  sourceType: z.enum([
    "payment_correction",
    "receivable_action",
    "financial_posting",
  ]),
  sourceId: entityIdSchema,
  sourceVersion: z.number().int().positive(),
  billingProfileId: entityIdSchema.nullable(),
  invoiceId: entityIdSchema.nullable(),
  paymentAttemptId: entityIdSchema.nullable(),
  providerEventId: z.string().trim().min(1).max(140).nullable(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  amountMinor: z.number().int().positive(),
  originalEventAt: z.iso.datetime({ offset: true }),
  postingDate: z.iso.date(),
  accountingMode: z.enum(["create_journal", "link_existing"]),
  existingDocument: existingDocumentSchema.nullable(),
  reason: z.string().trim().min(10).max(1_000),
  lines: z.array(financialPostingLineV1Schema).max(100),
}).strict().superRefine((value, context) => {
  if (value.accountingMode === "create_journal") {
    const debit = value.lines.reduce((sum, line) => sum + line.debitMinor, 0)
    const credit = value.lines.reduce((sum, line) => sum + line.creditMinor, 0)
    if (
      value.existingDocument !== null ||
      value.lines.length < 2 ||
      !Number.isSafeInteger(debit) ||
      debit !== credit ||
      debit !== value.amountMinor
    ) {
      context.addIssue({
        code: "custom",
        message: "A corrective journal must balance to the correction amount.",
      })
    }
  } else if (value.existingDocument === null || value.lines.length !== 0) {
    context.addIssue({
      code: "custom",
      message: "An existing correction must name exactly one accounting document.",
    })
  }
  if (
    value.sourceType === "payment_correction" &&
    value.providerEventId === null
  ) {
    context.addIssue({
      code: "custom",
      message: "Provider corrections require verified event provenance.",
    })
  }
})

export const financialCorrectionResultV1Schema = z.object({
  entityType: z.literal("financial_correction"),
  entityId: entityIdSchema,
  state: z.literal("posted"),
  version: z.literal(1),
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  kind: z.enum([
    "refund",
    "dispute",
    "chargeback",
    "write_off",
    "void",
    "operator_correction",
  ]),
  currency: z.string().regex(/^[A-Z]{3}$/),
  amountMinor: z.number().int().positive(),
  originalEventAt: z.iso.datetime({ offset: true }),
  postingDate: z.iso.date(),
  erpnextDocumentType: z.enum([
    "Journal Entry",
    "Payment Entry",
    "Sales Invoice",
  ]),
  erpnextDocumentId: z.string().trim().min(1).max(140),
  billingProfileId: entityIdSchema.nullable(),
  invoiceId: entityIdSchema.nullable(),
}).strict()

export type FinancialCorrectionV1 = z.infer<
  typeof financialCorrectionV1Schema
>
